import sumBy from 'lodash/sumBy'
import {getTimeSpan} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {fancyOperations} from '@freckle/educator-entities/ts/openapi-clients'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'

export type ClaimedFreeResourcesT = Omit<
  fancyOperations['getTeacherClaimedFreeResourcesR']['responses'][200]['content']['application/json'],
  'timeSpan'
>

export type ResourcesForCourseT = ClaimedFreeResourcesT['courses'][0]

export const fetchClaimedFreeResourcesForCurrentWeek = (
  teacherId: number
): Promise<ClaimedFreeResourcesT> =>
  fancyClient
    .GET('/2/teachers/{TeacherClaimedFreeResourcesP-TeacherId}/claimed-free-resources', {
      params: {
        query: getTimeSpan(),
        path: {'TeacherClaimedFreeResourcesP-TeacherId': teacherId}
      }
    })
    .then(extractData)

export function getDisabledCourseIds(
  currentSubject: SubjectT | null | undefined,
  claimedFreeResources: ClaimedFreeResourcesT
): Array<number> {
  const {maxFreeResourcesPerCourse, courses, premiumSubjects} = claimedFreeResources

  if (
    currentSubject !== null &&
    currentSubject !== undefined &&
    premiumSubjects.includes(currentSubject)
  ) {
    return []
  }

  const coursesWithNoResources = courses.filter(
    ({claimedFreeResources}) => claimedFreeResources >= maxFreeResourcesPerCourse
  )
  return coursesWithNoResources.map(({courseId}) => courseId)
}

export function getNumRemainingFreeResourcesForCourse(
  claimedFreeResources: ClaimedFreeResourcesT,
  courseId: number
): number | null {
  const {maxFreeResourcesPerCourse, courses} = claimedFreeResources
  const course = courses.find(resourcesForCourse => resourcesForCourse.courseId === courseId)
  if (course === undefined) {
    return null
  }
  return Math.max(maxFreeResourcesPerCourse - course.claimedFreeResources, 0)
}

export function hasRemainingFreeResourcesInAnyCourse(
  claimedFreeResources: ClaimedFreeResourcesT
): boolean {
  const {maxFreeResourcesPerCourse, courses} = claimedFreeResources
  const totalRemainingFreeResources = sumBy(
    courses,
    ({claimedFreeResources}) => maxFreeResourcesPerCourse - claimedFreeResources
  )
  return totalRemainingFreeResources > 0
}

export function canReassignInCourse(
  claimedFreeResources: ClaimedFreeResourcesT,
  courseId: number,
  isSubjectPremium: boolean
): boolean {
  if (isSubjectPremium) return true
  const resources = getNumRemainingFreeResourcesForCourse(claimedFreeResources, courseId)
  return resources === null || resources > 0
}
